import { type ComponentType } from 'react';

/** Infer props of either a React component or an intrinsic tag like 'button' */
export type PropsOf<C> = C extends keyof JSX.IntrinsicElements
  ? JSX.IntrinsicElements[C]
  : C extends ComponentType<infer P>
  ? P
  : never;

type PropsPart<C> = Partial<PropsOf<C>> & { $condition: boolean };

/**
 * Utility function to conditionally apply props based on a condition.
 * Returns the props from the first part where $condition is true, with $condition stripped out.
 */
export function conditionalProps<
  C extends ComponentType<any> | keyof JSX.IntrinsicElements,
  P extends PropsPart<C> = PropsPart<C>
>(...parts: P[]): Partial<PropsOf<C>> {
  const matchingPart = parts.find((p) => p.$condition);
  if (!matchingPart) {
    return {};
  }

  const { $condition, ...truthyProps } = matchingPart;
  return truthyProps as Partial<PropsOf<C>>;
}
